"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo, useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { DISPLAY_ONLY_QUESTION_TYPES } from "@/components/question-editor/field-type-config";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { getDefaultAnswerData, revealedQuestionDefaults } from "@/lib/note-answer-prefill";
import { mergeAnswersWithLocalChanges, noteEvaluationDate } from "@/lib/note-utils";
import { computeHiddenIds, noteAnswerValue } from "@/lib/question-conditions";
import { cx } from "@/lib/tailwind-utils";
import { QuestionType } from "@/types/forms/question";
import { NoteAnswerDetail, NoteDetail, NoteQuestionDetail } from "@/types/notes";
import { NoteAnswerEditor } from "./note-answer-editor";
import { NoteAnswerRenderer } from "./note-answer-renderer";
import { useNoteSectionCollapseStore } from "@/stores/note-section-collapse-store";
import { useTenantStore } from "@/stores/tenant-store";
function CollapsibleSection(t0) {
  const $ = _c(16);
  const {
    name,
    open,
    onOpenChange,
    children
  } = t0;
  const t1 = open && "rotate-90";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("h-4 w-4 shrink-0 text-foreground-muted transition-transform", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <FiChevronRight className={t2} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== name) {
    t4 = <span className="text-h3 text-foreground">{name}</span>;
    $[4] = name;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <CollapsibleTrigger className={NOTE_SECTION_HEADING_CLASS}>{t3}{t4}</CollapsibleTrigger>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children) {
    t6 = <CollapsibleContent><div className="pt-4">{children}</div></CollapsibleContent>;
    $[9] = children;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onOpenChange || $[12] !== open || $[13] !== t5 || $[14] !== t6) {
    t7 = <Collapsible open={open} onOpenChange={onOpenChange} className="col-span-full [&:not(:first-child)]:mt-4">{t5}{t6}</Collapsible>;
    $[11] = onOpenChange;
    $[12] = open;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}

/**
 * A section reads as a heading over its questions rather than a boxed card.
 * Shared with the note template editor so the template looks like the note.
 */
export const NOTE_SECTION_HEADING_CLASS = "flex w-full items-center gap-1.5 border-b border-border pb-2 text-left focus-visible:outline-none";

/** How a section lays out its questions for a given column count. */
export const noteSectionGridClass = (columns: number) => columns === 3 ? "grid grid-cols-3 items-stretch gap-x-8 gap-y-5" : columns === 1 ? "flex flex-col gap-5" : "grid grid-cols-2 items-stretch gap-x-8 gap-y-5";

// "Content" question types - if TEXT_AREA is the only one among these, make it full height
const CONTENT_QUESTION_TYPES = new Set([QuestionType.TEXT,
// short
QuestionType.TEXT_AREA,
// long
QuestionType.NUMBER,
// number
QuestionType.DATE,
// date
QuestionType.TOGGLE,
// yes/no
QuestionType.SINGLE_SELECT,
// single select
QuestionType.RADIAL_SINGLE_SELECT,
// single select (radio)
QuestionType.MULTI_SELECT,
// multiple choice
QuestionType.RADIAL_MULTI_SELECT // multiple choice (checkboxes)
]);

// Full-width question types that should span both columns
export const FULL_WIDTH_QUESTION_TYPES = new Set([QuestionType.SCRIBE_ATTACHMENTS, QuestionType.FILE_UPLOAD, QuestionType.MEDICATIONS, QuestionType.SOLD_ITEMS, QuestionType.SUPPLIES_USED, QuestionType.REBOOKING, QuestionType.BODY_MAP, QuestionType.SIGNATURE, QuestionType.DISCLAIMER, QuestionType.STATEMENT, QuestionType.TABLE]);

/**
 * The one top-level question whose answer the note stretches to full height: a
 * long text that is the only top-level "content" question.
 */
export function loneTopLevelTextAreaId(questions: {
  id: string;
  sectionId?: string | null;
  questionType: QuestionType;
}[]): string | null {
  const content = questions.filter(q => !q.sectionId && CONTENT_QUESTION_TYPES.has(q.questionType));
  return content.length === 1 && content[0].questionType === QuestionType.TEXT_AREA ? content[0].id : null;
}

/** Whether a question's answer fills its cell when it sits in a section. */
export const fillsSectionCell = (questionType: QuestionType) => !FULL_WIDTH_QUESTION_TYPES.has(questionType) && questionType !== QuestionType.TEXT_AREA;
interface NoteAnswersSectionProps {
  note: NoteDetail;
  isEditing?: boolean;
  onAnswerChange?: (answerId: string, data: Record<string, any>, noteAttachmentId?: string) => void;
  localAnswerChanges?: Record<string, Record<string, any>>; // Local state for unsaved changes
  // When true, disable every question editor EXCEPT SCRIBE_ATTACHMENTS and
  // MEDICATIONS so the user can still upload images and record doses while the
  // rest of the template waits for AI generation.
  disabled?: boolean;
  /** A template preview with no saved note behind it: editors get no note or
   *  patient, so nothing that loads or writes by id runs. */
  preview?: boolean;
}

// Question types that stay interactive even when the section is `disabled`.
// Images and doses are captured during the visit, before AI generation runs.
const ALWAYS_INTERACTIVE_TYPES = new Set<QuestionType>([QuestionType.SCRIBE_ATTACHMENTS, QuestionType.MEDICATIONS]);

// When the section is `disabled` (e.g. waiting on AI generation), these
// dropdowns stay openable so the user can peek at the options, but their
// options are locked and can't be selected. They must not be wrapped in
// `pointer-events-none`, or the trigger couldn't open.
const PEEKABLE_WHEN_DISABLED_TYPES = new Set<QuestionType>([QuestionType.SINGLE_SELECT, QuestionType.MULTI_SELECT]);
type SingleGroup = {
  type: "single";
  question: NoteQuestionDetail;
};
type SectionGroup = {
  type: "section";
  sectionId: string;
  sectionName: string;
  questions: NoteQuestionDetail[];
};
type QuestionGroup = SingleGroup | SectionGroup;

// Attachment questions load their images by note, not from the answer, so they
// keep one key when their answer is created. A new key remounts the grid and
// closes a face chart the provider is annotating.
const questionKey = (question: NoteQuestionDetail, answerMap: Map<string, NoteAnswerDetail>): string => question.questionType === QuestionType.SCRIBE_ATTACHMENTS ? `question-${question.id}` : answerMap.get(question.id)?.id ?? `question-${question.id}`;

// A fully built top-level entry ready to render. Section bodies are built once
// (in the memo below) so toggling collapse state reuses the same element refs
// and React skips re-rendering the answer editors/renderers inside them.
type RenderedEntry = {
  kind: "section";
  sectionId: string;
  name: string;
  key: string;
  body: React.ReactNode;
} | {
  kind: "single";
  node: React.ReactNode;
};

// Stable identities so the render memo's deps don't change every render when a
// caller omits these props (a fresh `{}` default would defeat the memo).
const EMPTY_LOCAL_CHANGES: Record<string, Record<string, any>> = {};
const EMPTY_COLLAPSED: Record<string, boolean> = {};
export const NoteAnswersSection: React.FC<NoteAnswersSectionProps> = t0 => {
  const $ = _c(53);
  const {
    note,
    isEditing: t1,
    onAnswerChange,
    localAnswerChanges: t2,
    disabled: t3,
    preview: t4
  } = t0;
  const isEditing = t1 === undefined ? false : t1;
  const localAnswerChanges = t2 === undefined ? EMPTY_LOCAL_CHANGES : t2;
  const disabled = t3 === undefined ? false : t3;
  const preview = t4 === undefined ? false : t4;
  const tenant = useTenantStore(_temp);
  const noteRootId = note.parentNoteId || note.id;
  let t5;
  if ($[0] !== noteRootId || $[1] !== tenant) {
    t5 = JSON.stringify([tenant, noteRootId]);
    $[0] = noteRootId;
    $[1] = tenant;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const collapseScope = t5;
  let t6;
  if ($[3] !== collapseScope) {
    t6 = state_0 => state_0.collapsedByNote[collapseScope] ?? EMPTY_COLLAPSED;
    $[3] = collapseScope;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const savedCollapsedSections = useNoteSectionCollapseStore(t6);
  const updateSavedCollapsed = useNoteSectionCollapseStore(_temp2);
  let t7;
  if ($[5] !== note.id) {
    t7 = () => ({
      noteId: note.id,
      sections: {}
    });
    $[5] = note.id;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const [collapseState, setCollapseState] = useState(t7);
  let t8;
  if ($[7] !== localAnswerChanges || $[8] !== note || $[9] !== onAnswerChange) {
    t8 = (answerId, data, noteAttachmentId) => {
      if (!onAnswerChange) {
        return;
      }
      onAnswerChange(answerId, data, noteAttachmentId);
      const defaults = revealedQuestionDefaults(note, localAnswerChanges, {
        ...localAnswerChanges,
        [answerId]: data
      });
      for (const revealed of defaults) {
        onAnswerChange(revealed.answerId, revealed.data);
      }
    };
    $[7] = localAnswerChanges;
    $[8] = note;
    $[9] = onAnswerChange;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleAnswerChange = t8;
  let t9;
  if ($[11] !== disabled || $[12] !== handleAnswerChange || $[13] !== isEditing || $[14] !== localAnswerChanges || $[15] !== note || $[16] !== onAnswerChange || $[17] !== preview) {
    bb0: {
      if (!note.template || !note.template.questions || note.template.questions.length === 0) {
        t9 = null;
        break bb0;
      }
      const questionMap = new Map();
      note.template.questions.forEach(q => {
        questionMap.set(q.id, q);
      });
      const answerMap = new Map(mergeAnswersWithLocalChanges(note, localAnswerChanges).filter(_temp3).map(_temp4));
      const evalCtx = {
        getValue: dependentQuestionId => noteAnswerValue(answerMap.get(dependentQuestionId), questionMap.get(dependentQuestionId)?.questionType),
        getQuestionType: dependentQuestionId_0 => questionMap.get(dependentQuestionId_0)?.questionType,
        failOpenOnUnknownDependency: true,
        evaluationDate: noteEvaluationDate(note)
      };
      const hidden = computeHiddenIds(note.template.questions, note.template.sections ?? [], evalCtx);
      const sortedQuestions = [...note.template.questions].filter(q_0 => !hidden.questionIds.has(q_0.id)).sort(_temp5);
      if (sortedQuestions.length === 0) {
        t9 = null;
        break bb0;
      }
      let t10;
      if ($[19] !== note.template.sections) {
        t10 = new Set(note.template?.sections?.map(_temp6) ?? []);
        $[19] = note.template.sections;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      const loadedSectionIds = t10;
      let t11;
      if ($[21] !== loadedSectionIds) {
        t11 = q_1 => q_1.sectionId && !loadedSectionIds.has(q_1.sectionId);
        $[21] = loadedSectionIds;
        $[22] = t11;
      } else {
        t11 = $[22];
      }
      if (sortedQuestions.some(t11)) {
        t9 = null;
        break bb0;
      }
      const fullHeightQuestionId = loneTopLevelTextAreaId(sortedQuestions);
      const sectionColumnsMap = new Map();
      if (note.template?.sections) {
        for (const section of note.template.sections) {
          sectionColumnsMap.set(section.id, section.columns);
        }
      }
      const questionsBySectionId = new Map();
      const unsectionedQuestions = [];
      for (const question of sortedQuestions) {
        if (question.sectionId) {
          if (!questionsBySectionId.has(question.sectionId)) {
            questionsBySectionId.set(question.sectionId, []);
          }
          questionsBySectionId.get(question.sectionId).push(question);
        } else {
          unsectionedQuestions.push(question);
        }
      }
      const topLevelEntries = [...(note.template?.sections ?? []).map(_temp7), ...unsectionedQuestions.map(_temp8)];
      topLevelEntries.sort(_temp9);
      const questionGroups = [];
      for (const entry of topLevelEntries) {
        if (entry.kind === "section") {
          const questions = questionsBySectionId.get(entry.section.id);
          if (questions && questions.length > 0) {
            questionGroups.push({
              type: "section",
              sectionId: entry.section.id,
              sectionName: entry.section.name,
              questions
            });
          }
        } else {
          questionGroups.push({
            type: "single",
            question: entry.question
          });
        }
      }
      const buildContent = (question_1, shouldBeFullHeight) => {
        const answer = answerMap.get(question_1.id);
        if (isEditing && onAnswerChange) {
          const tempAnswerId = `temp-${question_1.id}`;
          const localChanges = localAnswerChanges[tempAnswerId];
          const defaultData = localChanges ? {} : getDefaultAnswerData(question_1);
          const resolvedAnswer = answer ?? {
            id: tempAnswerId,
            noteId: note.id,
            questionId: question_1.id,
            data: localChanges || defaultData,
            createdDate: new Date().toISOString()
          };
          const questionDisabled = disabled && !ALWAYS_INTERACTIVE_TYPES.has(question_1.questionType);
          const showPatientReportedPrefill = Boolean(answer?.isUserReported && !localAnswerChanges[answer.id]);
          const editor = <NoteAnswerEditor answer={resolvedAnswer} question={question_1} onChange={handleAnswerChange} noteId={preview ? undefined : note.id} patientId={preview ? undefined : note.patient.id} fullHeight={shouldBeFullHeight} disabled={questionDisabled} showPatientReportedPrefill={showPatientReportedPrefill} />;
          const peekable = PEEKABLE_WHEN_DISABLED_TYPES.has(question_1.questionType);
          return questionDisabled ? <div className={peekable ? "opacity-60" : "pointer-events-none opacity-60"}>{editor}</div> : editor;
        } else {
          const questionName = (question_1.data as {
            name?: string;
          }).name;
          if (DISPLAY_ONLY_QUESTION_TYPES.has(question_1.questionType)) {
            const displayAnswer = answer ?? {
              id: `display-${question_1.id}`,
              noteId: note.id,
              questionId: question_1.id,
              data: {},
              createdDate: new Date().toISOString()
            };
            return <div className="space-y-1">{questionName && <p className="text-overline text-foreground-muted">{questionName}</p>}<NoteAnswerRenderer answer={displayAnswer} question={question_1} noteId={note.id} patientId={note.patient.id} /></div>;
          }
          if (!answer) {
            if (!question_1.required) {
              return null;
            }
            return <div className="space-y-1">{questionName && <p className="text-overline text-foreground-muted">{questionName}<span className="ml-1 text-destructive">*</span></p>}<div className="text-body italic text-foreground-muted">Not answered</div></div>;
          }
          return <div className="space-y-1">{questionName && <p className="text-overline text-foreground-muted">{questionName}</p>}<NoteAnswerRenderer answer={answer} question={question_1} noteId={note.id} patientId={note.patient.id} /></div>;
        }
      };
      const entries = [];
      const sectionIds = [];
      questionGroups.forEach(group => {
        if (group.type === "section") {
          const sectionColumns = sectionColumnsMap.get(group.questions[0]?.sectionId ?? "") ?? 2;
          const sectionGridClass = noteSectionGridClass(sectionColumns);
          const sectionItems = group.questions.map(question_2 => {
            const isFullWidth = FULL_WIDTH_QUESTION_TYPES.has(question_2.questionType);
            const content = buildContent(question_2, fillsSectionCell(question_2.questionType));
            if (content === null) {
              return null;
            }
            const key = questionKey(question_2, answerMap);
            return <div key={key} className={cx("flex flex-col", isFullWidth && "col-span-full")}>{content}</div>;
          }).filter(Boolean);
          if (sectionItems.length === 0) {
            return;
          }
          sectionIds.push(group.sectionId);
          entries.push({
            kind: "section",
            sectionId: group.sectionId,
            name: group.sectionName,
            key: `section-${group.sectionId}`,
            body: <div className={sectionGridClass}>{sectionItems}</div>
          });
          return;
        }
        const {
          question: question_3
        } = group;
        const shouldBeFullHeight_0 = question_3.id === fullHeightQuestionId;
        const content_0 = buildContent(question_3, shouldBeFullHeight_0);
        if (content_0 === null) {
          return;
        }
        const key_0 = questionKey(question_3, answerMap);
        entries.push({
          kind: "single",
          node: <div key={key_0} className={cx(shouldBeFullHeight_0 && "flex flex-1 flex-col")}>{content_0}</div>
        });
      });
      let t12;
      if ($[23] !== entries || $[24] !== sectionIds) {
        t12 = {
          entries,
          sectionIds
        };
        $[23] = entries;
        $[24] = sectionIds;
        $[25] = t12;
      } else {
        t12 = $[25];
      }
      t9 = t12;
    }
    $[11] = disabled;
    $[12] = handleAnswerChange;
    $[13] = isEditing;
    $[14] = localAnswerChanges;
    $[15] = note;
    $[16] = onAnswerChange;
    $[17] = preview;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const rendered = t9;
  if (!rendered) {
    return null;
  }
  const {
    entries: entries_0,
    sectionIds: sectionIds_0
  } = rendered;
  const collapsedSections = preview ? collapseState.noteId === note.id ? collapseState.sections : EMPTY_COLLAPSED : savedCollapsedSections;
  let t10;
  if ($[26] !== collapsedSections) {
    t10 = sectionId => !collapsedSections[sectionId];
    $[26] = collapsedSections;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const isSectionOpen = t10;
  let t11;
  if ($[28] !== isSectionOpen || $[29] !== sectionIds_0) {
    t11 = sectionIds_0.some(isSectionOpen);
    $[28] = isSectionOpen;
    $[29] = sectionIds_0;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const anySectionOpen = t11;
  let t12;
  if ($[31] !== collapseScope || $[32] !== note || $[33] !== preview || $[34] !== updateSavedCollapsed) {
    t12 = updater => {
      if (preview) {
        setCollapseState(prev => {
          const base = prev.noteId === note.id ? prev.sections : {};
          return {
            noteId: note.id,
            sections: updater(base)
          };
        });
      } else {
        updateSavedCollapsed(collapseScope, updater);
      }
    };
    $[31] = collapseScope;
    $[32] = note;
    $[33] = preview;
    $[34] = updateSavedCollapsed;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const updateCollapsed = t12;
  let t13;
  if ($[36] !== sectionIds_0 || $[37] !== updateCollapsed) {
    t13 = collapsed => {
      updateCollapsed(base_0 => {
        const next = {
          ...base_0
        };
        for (const id of sectionIds_0) {
          next[id] = collapsed;
        }
        return next;
      });
    };
    $[36] = sectionIds_0;
    $[37] = updateCollapsed;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const setAllSectionsCollapsed = t13;
  let t14;
  if ($[39] !== anySectionOpen || $[40] !== sectionIds_0.length || $[41] !== setAllSectionsCollapsed) {
    t14 = sectionIds_0.length > 1 && <div className="flex justify-end"><button type="button" onClick={() => setAllSectionsCollapsed(anySectionOpen)} className="flex items-center gap-1 rounded-lg px-2 py-1 text-body-emphasis text-foreground-muted transition-colors hover:bg-hover focus-visible:outline-none">{anySectionOpen ? <FiChevronRight className="h-3.5 w-3.5 shrink-0" /> : <FiChevronDown className="h-3.5 w-3.5 shrink-0" />}<span>{anySectionOpen ? "Collapse all" : "Expand all"}</span></button></div>;
    $[39] = anySectionOpen;
    $[40] = sectionIds_0.length;
    $[41] = setAllSectionsCollapsed;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== entries_0 || $[44] !== isSectionOpen || $[45] !== updateCollapsed) {
    let t16;
    if ($[47] !== isSectionOpen || $[48] !== updateCollapsed) {
      t16 = entry_0 => {
        if (entry_0.kind === "section") {
          return <CollapsibleSection key={entry_0.key} name={entry_0.name} open={isSectionOpen(entry_0.sectionId)} onOpenChange={nextOpen => updateCollapsed(base_1 => ({
            ...base_1,
            [entry_0.sectionId]: !nextOpen
          }))}>{entry_0.body}</CollapsibleSection>;
        }
        return entry_0.node;
      };
      $[47] = isSectionOpen;
      $[48] = updateCollapsed;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    t15 = entries_0.map(t16);
    $[43] = entries_0;
    $[44] = isSectionOpen;
    $[45] = updateCollapsed;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[50] !== t14 || $[51] !== t15) {
    t16 = <div className="flex flex-col gap-5">{t14}{t15}</div>;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  return t16;
};
function _temp(state) {
  return state.currentTenant;
}
function _temp2(state_1) {
  return state_1.updateCollapsed;
}
function _temp3(a) {
  return !(a as {
    isArchived?: boolean;
  }).isArchived;
}
function _temp4(a_0) {
  return [a_0.questionId, a_0];
}
function _temp5(a_1, b) {
  return a_1.position - b.position;
}
function _temp6(s) {
  return s.id;
}
function _temp7(section_0) {
  return {
    kind: "section",
    section: section_0
  } as TopLevelEntry;
}
function _temp8(question_0) {
  return {
    kind: "question",
    question: question_0
  } as TopLevelEntry;
}
function _temp9(a_2, b_0) {
  const ap = a_2.kind === "section" ? a_2.section.position : a_2.question.position;
  const bp = b_0.kind === "section" ? b_0.section.position : b_0.question.position;
  if (ap !== bp) {
    return ap - bp;
  }
  if (a_2.kind === b_0.kind) {
    return 0;
  }
  return a_2.kind === "section" ? -1 : 1;
}